import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PasswordInput from '@/components/PasswordInput';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { ArrowLeft, Plus, UserPlus, Trash2 } from 'lucide-react';

const UserManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({ email: '', full_name: '', password: '', role: 'hiring_manager' });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try { const res = await api.listUsers(); setUsers(res.data); }
    catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.registerUser({ ...formData, organisation_id: user.organisation_id });
      toast.success('User created');
      setShowAdd(false);
      setFormData({ email: '', full_name: '', password: '', role: 'hiring_manager' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.deleteUser(deleteTarget.id);
      toast.success(`Removed ${deleteTarget.full_name || deleteTarget.email}`);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete this user');
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.updateUser(u.id, { is_active: !u.is_active });
      toast.success(`User ${u.is_active ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      <div className="max-w-5xl mx-auto p-8">
        <Button onClick={() => navigate('/dashboard')} variant="outline" className="rounded-md mb-4" data-testid="back-button">
          <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
        </Button>

        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="users-title">User Management</h1>
            <p className="text-sm text-neutral-500 mt-1">Manage HR admins and hiring managers</p>
          </div>

          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button className="bg-brand hover:bg-brand-dark text-white rounded-md" data-testid="add-user-button">
                <UserPlus className="h-4 w-4 mr-2" /> Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add New User</DialogTitle></DialogHeader>
              <form onSubmit={handleAdd} className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label>Full Name *</Label>
                  <Input value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} required className="rounded-md" data-testid="new-user-name" />
                </div>
                <div className="space-y-1.5">
                  <Label>Email *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="rounded-md" data-testid="new-user-email" />
                </div>
                <div className="space-y-1.5">
                  <Label>Password *</Label>
                  <PasswordInput value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required className="rounded-md" data-testid="new-user-password" />
                </div>
                <div className="space-y-1.5">
                  <Label>Role *</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                    <SelectTrigger className="rounded-md" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hiring_manager">Hiring Manager</SelectItem>
                      <SelectItem value="hr_admin">HR Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-brand hover:bg-brand-dark text-white rounded-md" data-testid="submit-user-button">
                  Create User
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-lg border border-neutral-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell><span className="text-xs">{u.role.replace('_', ' ')}</span></TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${u.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-neutral-100 text-neutral-500 border-neutral-200'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => toggleActive(u)} className="rounded-md text-xs" data-testid={`toggle-user-${u.id}`}>
                        {u.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      {u.id !== user?.id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteTarget(u)}
                          aria-label={`Delete ${u.full_name || u.email}`}
                          title={`Delete ${u.full_name || u.email}`}
                          className="rounded-md text-[#B4552F] border-[#B4552F]/30 hover:bg-[#B4552F]/10 hover:text-[#B4552F] px-2"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    
      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="rounded-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle>Remove this user?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#5C665F]">
            <p>
              <span className="font-medium text-[#1A231C]">{deleteTarget?.full_name}</span>
              {deleteTarget?.email ? ` - ${deleteTarget.email}` : ''}
            </p>
            <p>
              They will be signed out and will no longer be able to log in.
            </p>
            <p className="text-xs">
              Assessments they created stay where they are, and the audit log still
              shows what they did. To block access temporarily instead, use Deactivate.
            </p>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" className="rounded-md" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              className="bg-[#B4552F] hover:bg-[#93441F] text-white rounded-md"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-user"
            >
              {deleting ? 'Removing...' : 'Remove user'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
